// SSE consumer for live chat session wake-up notifications.
// Wakes the chat UI within ~200-400ms of a new message instead
// of waiting for the next 8s poll tick.
//
// Two deploy shapes -- one consumer contract:
//
//   * Express BFF (classic-fdn-app): the BFF holds the SSE proxy,
//     subscribed to raindb-api's /api/v1/wire/subscribe with the
//     platform key, and re-emits each upstream `wakeup` event as
//     a `session-advanced` event tagged with the resolved
//     sessionId. The browser opens
//     `/api/p/<pid>/wire/subscribe?sessionIds=...` (BFF
//     same-origin) and consumes session-advanced events.
//
//   * Lightning bolt deploy: the bolt CAN'T hold long-lived
//     connections (goja is request/response). The bolt instead
//     mints a narrow IAM token via ctx.iam.mintWireToken at
//     subscribe time; the browser carries the token to the
//     bolt's same-origin /api/v1/wire/subscribe (served by the
//     lightning dispatcher's RouteMatchSSE arm); the platform's
//     pubsub.ServeSSE delivers `wakeup` SSE events directly.
//     The browser-side handler translates each wakeup's `key`
//     field back to the originating sessionId via the mint
//     response's keys[] -> sessionIds[] index.
//
// Phase 7 of the Lightning SSE feature work
// (raindb-phoenix-lightning docs/RAINDB_PHX_LIGHTNING_SSE.md J)
// added the bolt-mode path and made it the default. The BFF-mode
// path is preserved as a runtime fallback when the bolt-mint
// endpoint isn't deployed (older bolt revisions; classic-fdn-app
// where the BFF endpoint exists at the same wire shape).
//
// chatStore depends on the consumer contract -- subscribeToSessions
// returns a cancel fn; the onSessionAdvanced callback fires once
// per upstream wakeup with the resolved sessionId. Both deploy
// shapes satisfy that contract; the implementation is selected at
// subscribe time by trying the bolt mint first and falling back
// to the BFF stream on 404.
import { openSSE } from './api';
/**
 * toRelativeKey strips a leading `tenants/<tenantId>/` prefix from a
 * physical resource key, yielding the tenant-relative shape.
 *
 * We subscribe with tenant-relative keys (the platform owns + applies
 * the tenants/<tid>/ prefix -- raindb-prime auth.ScopeResourceID), so
 * our key -> sessionId map is keyed on the relative shape. The wakeup
 * SSE event, however, carries the PHYSICAL invalidation key. Normalize
 * back to relative so the map lookup matches. A key that is already
 * relative (no `tenants/` prefix) passes through unchanged.
 */
function toRelativeKey(key) {
    if (!key.startsWith('tenants/'))
        return key;
    // tenants/<tenantId>/<relative...> -> <relative...>
    const rest = key.slice('tenants/'.length);
    const slash = rest.indexOf('/');
    return slash >= 0 ? rest.slice(slash + 1) : key;
}
/**
 * Subscribe to wake-up notifications for the given session ids
 * under the given project. Returns a cancel function that
 * tears down the underlying stream.
 *
 * Tries the bolt-mint flow first. If the bolt's POST
 * /api/p/:pid/wire-token endpoint returns 404 (older revision or
 * BFF deploy), falls back to the BFF SSE stream at
 * /api/p/:pid/wire/subscribe?sessionIds=. Both deliver the same
 * onSessionAdvanced semantics.
 *
 * onError fires on connection error (network, upstream rejection,
 * etc). Callers reopen with whatever the right key set is at that
 * time (after a short backoff). Neither flow auto-reconnects.
 */
export function subscribeToSessions(projectId, sessionIds, onSessionAdvanced, onError) {
    if (!projectId || sessionIds.length === 0) {
        // Nothing to subscribe to. Return a no-op cancel.
        return () => undefined;
    }
    // Holder lets us mutate the cancel function asynchronously --
    // openBoltSSE is async so we have to return a sync cancel
    // immediately while the mint POST is in flight.
    let inner = null;
    let cancelled = false;
    void openBoltSSE(projectId, sessionIds, onSessionAdvanced, onError)
        .then((cancel) => {
        if (cancelled) {
            cancel();
            return;
        }
        inner = cancel;
    })
        .catch((err) => {
        // 404 = no bolt-mint endpoint, fall back to BFF stream.
        const is404 = /HTTP 404/.test(err.message);
        if (!is404) {
            onError?.(err);
            return;
        }
        if (cancelled)
            return;
        const cancel = openBffSSE(projectId, sessionIds, onSessionAdvanced, onError);
        if (cancelled) {
            cancel();
            return;
        }
        inner = cancel;
    });
    return () => {
        cancelled = true;
        inner?.();
        inner = null;
    };
}
// ---------------------------------------------------------------
// Bolt mode -- mint wire-key token + open EventSource directly
// ---------------------------------------------------------------
async function openBoltSSE(projectId, sessionIds, onSessionAdvanced, onError) {
    // Mint the wire-key token. Same-origin POST so credentials carry
    // automatically (the bolt's auth middleware reads the
    // fdn_token cookie set at login).
    const mintResp = await fetch(`/api/p/${encodeURIComponent(projectId)}/wire-token`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ sessionIds }),
    });
    if (!mintResp.ok) {
        let detail = '';
        try {
            const body = await mintResp.json();
            detail = typeof body?.error === 'string' ? body.error : '';
        }
        catch {
            // No body or non-JSON; that's fine.
        }
        throw new Error(`wire-token mint failed: HTTP ${mintResp.status}${detail ? ' (' + detail + ')' : ''}`);
    }
    const minted = (await mintResp.json());
    // Build a key -> sessionId lookup so wakeup events can be
    // translated back. The bolt's mint preserves the order it
    // received sessionIds -- defensive: pair by index.
    const keyToSession = new Map();
    for (let i = 0; i < minted.keys.length; i++) {
        const k = minted.keys[i];
        const sid = minted.sessionIds[i];
        if (k && sid)
            keyToSession.set(k, sid);
    }
    // EventSource API can't set custom headers on the initial GET,
    // so the platform's serveSSE accepts ?token= as the canonical
    // browser path. The lightning dispatcher's RouteMatchSSE arm
    // pulls the token off the query string + validates against the
    // wire-key resources in the grant.
    const endpoint = minted.endpoint || '/api/v1/wire/subscribe';
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('token', minted.token);
    for (const k of minted.keys) {
        url.searchParams.append('key', k);
    }
    const es = new EventSource(url.toString());
    let active = true;
    // ready -- the platform sent an initial event confirming the
    // subscription registered. Useful for diagnostics; no app
    // action.
    es.addEventListener('ready', (_ev) => {
        // intentional no-op
    });
    // wakeup -- the platform's wire mesh saw a write to one of our
    // chain-head keys. Translate to sessionId + dispatch.
    //
    // KEY SHAPE: we subscribe with TENANT-RELATIVE keys (the platform
    // applies the tenants/<tid>/ prefix at mint + subscribe), so
    // keyToSession is keyed on the RELATIVE shape. But the wakeup
    // event's `key` is the PHYSICAL invalidation key
    // (tenants/<tid>/indexes/...) -- the wire mesh fans out against
    // physical ObjectCache keys (raindb-prime pubsub/sse_serve.go).
    // Normalize the incoming key to tenant-relative before the lookup,
    // and fall back to the raw key, so the translation works whether
    // the platform emits the physical or the relative form. Without
    // this the lookup misses every wakeup and delivery silently
    // degrades to the 30s poll backstop.
    es.addEventListener('wakeup', (ev) => {
        try {
            const payload = JSON.parse(ev.data);
            const key = payload?.key;
            if (!key)
                return;
            const sid = keyToSession.get(toRelativeKey(key)) ?? keyToSession.get(key);
            if (!sid)
                return;
            const ts = typeof payload?.ts === 'number' ? payload.ts : undefined;
            onSessionAdvanced({ sessionId: sid, ts });
        }
        catch (err) {
            onError?.(err instanceof Error ? err : new Error(String(err)));
        }
    });
    // heartbeat -- keepalive frame from the platform. Silent on
    // the consumer side; the EventSource auto-handles the underlying
    // TCP keepalive.
    es.addEventListener('heartbeat', (_ev) => {
        // intentional no-op
    });
    // error -- EventSource closes on any transport failure. Common
    // causes: token expiry (platform refuses next subscribe), peer
    // disconnect, host shutdown. caller's reopen logic decides
    // whether to re-mint immediately or back off.
    es.onerror = (_ev) => {
        if (!active)
            return;
        active = false;
        onError?.(new Error('wire-subscribe stream closed (likely token expiry)'));
    };
    return () => {
        if (active) {
            active = false;
            es.close();
        }
    };
}
// ---------------------------------------------------------------
// BFF mode -- legacy fallback for classic-fdn-app deploys
// ---------------------------------------------------------------
function openBffSSE(projectId, sessionIds, onSessionAdvanced, onError) {
    const ids = sessionIds.join(',');
    const cancel = openSSE(`/p/${projectId}/wire/subscribe?sessionIds=${encodeURIComponent(ids)}`, (event, data) => {
        if (event === 'session-advanced') {
            const payload = data;
            if (payload.sessionId) {
                onSessionAdvanced({ sessionId: payload.sessionId, ts: payload.ts });
            }
        }
        // ready + heartbeat events are silent on the consumer side.
    }, onError);
    return cancel;
}
